import moment, { type Moment } from "moment";
import {
  ContentTypeType,
  PollStatusType,
  PollTriggerType,
  type ContentPollScheduleInputType,
} from "@/types/graphql/partner.generated";
import type { ContentPoll } from "@/partner/entities/polls";

/** Пределы совпадают с бэкендом (ContentPollValidator). */
export const POLL_MIN_AUTO_CLOSE = 10;
export const POLL_MAX_AUTO_CLOSE = 3600;

/** Прикрепление опроса библиотеки к видео или эфиру. */
export interface ContentPollFormValues {
  /** Опрос библиотеки; пустая строка, пока не выбран. */
  pollId: string;
  trigger: PollTriggerType;
  /** «мм:сс» или целые минуты. */
  offset: string;
  /** Время запуска из пикера; прошедшее допустимо (опрос откроется сразу). */
  startsAt: Moment | null;
  autoCloseSeconds: string;
}

export const POLL_TRIGGER_LABEL_KEYS: Record<PollTriggerType, string> = {
  [PollTriggerType.ManualType]: "polls.trigger.manual",
  [PollTriggerType.AtOffsetType]: "polls.trigger.atOffset",
  [PollTriggerType.AtTimeType]: "polls.trigger.atTime",
};

export const POLL_STATUS_LABEL_KEYS: Record<PollStatusType, string> = {
  [PollStatusType.DraftType]: "polls.status.draft",
  [PollStatusType.ScheduledType]: "polls.status.scheduled",
  [PollStatusType.ActiveType]: "polls.status.active",
  [PollStatusType.ClosedType]: "polls.status.closed",
};

export type ContentPollFormError =
  | "pollRequired"
  | "offsetInvalid"
  | "startsAtRequired"
  | "autoCloseInvalid";

export const CONTENT_POLL_FORM_ERROR_KEYS: Record<
  ContentPollFormError,
  string
> = {
  pollRequired: "polls.errors.pollRequired",
  offsetInvalid: "polls.errors.offsetInvalid",
  startsAtRequired: "polls.errors.startsAtRequired",
  autoCloseInvalid: "polls.errors.autoCloseInvalid",
};

const isVideo = (contentType: ContentTypeType) =>
  contentType === ContentTypeType.VideoType;

export function emptyContentPollForm(
  contentType: ContentTypeType
): ContentPollFormValues {
  return {
    pollId: "",
    trigger: isVideo(contentType)
      ? PollTriggerType.AtOffsetType
      : PollTriggerType.ManualType,
    offset: "",
    startsAt: null,
    autoCloseSeconds: "",
  };
}

export function formatOffset(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

export function parseOffset(value: string): number | null {
  const match = /^(\d+)(?::([0-5]\d))?$/.exec(value.trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2] ?? 0);
}

export function contentPollToFormValues(
  poll: Pick<
    ContentPoll,
    "pollId" | "trigger" | "offsetSeconds" | "startsAt" | "autoCloseSeconds"
  >
): ContentPollFormValues {
  return {
    pollId: poll.pollId,
    trigger: poll.trigger,
    offset:
      poll.offsetSeconds !== null && poll.offsetSeconds !== undefined
        ? formatOffset(poll.offsetSeconds)
        : "",
    startsAt: poll.startsAt ? moment(poll.startsAt) : null,
    autoCloseSeconds:
      poll.autoCloseSeconds !== null && poll.autoCloseSeconds !== undefined
        ? String(poll.autoCloseSeconds)
        : "",
  };
}

const effectiveTrigger = (
  values: ContentPollFormValues,
  contentType: ContentTypeType
) => (isVideo(contentType) ? PollTriggerType.AtOffsetType : values.trigger);

export function validateContentPollForm(
  values: ContentPollFormValues,
  contentType: ContentTypeType
): ContentPollFormError | null {
  if (!values.pollId) return "pollRequired";

  const trigger = effectiveTrigger(values, contentType);
  if (
    trigger === PollTriggerType.AtOffsetType &&
    parseOffset(values.offset) === null
  ) {
    return "offsetInvalid";
  }
  if (trigger === PollTriggerType.AtTimeType && !values.startsAt?.isValid()) {
    return "startsAtRequired";
  }
  if (!isVideo(contentType) && values.autoCloseSeconds.trim()) {
    const seconds = Number(values.autoCloseSeconds);
    if (
      !Number.isInteger(seconds) ||
      seconds < POLL_MIN_AUTO_CLOSE ||
      seconds > POLL_MAX_AUTO_CLOSE
    ) {
      return "autoCloseInvalid";
    }
  }
  return null;
}

export function toScheduleInput(
  values: ContentPollFormValues,
  contentType: ContentTypeType
): ContentPollScheduleInputType {
  const trigger = effectiveTrigger(values, contentType);
  return {
    trigger,
    offsetSeconds:
      trigger === PollTriggerType.AtOffsetType
        ? parseOffset(values.offset)
        : null,
    startsAt:
      trigger === PollTriggerType.AtTimeType && values.startsAt?.isValid()
        ? values.startsAt.toISOString()
        : null,
    autoCloseSeconds:
      !isVideo(contentType) && values.autoCloseSeconds.trim()
        ? Number(values.autoCloseSeconds)
        : null,
  };
}

/**
 * Перезапрашивать ли список по таймеру. Прикрепление видео открыто всегда;
 * у эфира ждём результаты открытого опроса, а на идущем эфире ещё и
 * автооткрытие запланированного (подписок у партнёра нет).
 */
export function shouldPollResults(params: {
  contentType: ContentTypeType;
  streamLive: boolean;
  statuses: (PollStatusType | null | undefined)[];
}): boolean {
  const { contentType, streamLive, statuses } = params;
  if (contentType !== ContentTypeType.StreamType) return statuses.length > 0;
  return statuses.some(
    (status) =>
      status === PollStatusType.ActiveType ||
      (streamLive && status === PollStatusType.ScheduledType)
  );
}

/** То же правило, что у бэкенда (isContentPollEditable в домене polls). */
export function isContentPollEditable(
  poll: Pick<ContentPoll, "status" | "votersCount">,
  contentType: ContentTypeType
): boolean {
  if (contentType === ContentTypeType.StreamType) {
    return (
      poll.status === PollStatusType.DraftType ||
      poll.status === PollStatusType.ScheduledType
    );
  }
  return poll.votersCount === 0;
}

export function pollPercent(
  votes: number | null | undefined,
  votersCount: number | null | undefined
): number {
  if (!votes || !votersCount) return 0;
  return Math.round((votes / votersCount) * 100);
}
